import React from 'react';
import { cn } from '../../lib/utils';
import snakeToPascal from '../../tools';

interface ConceptHeaderProps {
    item: any;
    tree: { items: Record<string, any>; rootId: string };
    onBreadcrumbClick: (id: string) => void;
    onDocumentationClick: () => void;
}

const buildBreadcrumb = (item: any, tree: any): Array<{ id: string; name: string }> => {
    const crumbs: Array<{ id: string; name: string }> = [];
    let current = item;
    while (current && current.parentId) {
        current = tree.items[current.parentId];
        if (current && current.id !== tree.rootId) {
            crumbs.unshift({ id: current.id, name: current.name });
        }
    }
    return crumbs;
};

export default function ConceptHeader({
    item,
    tree,
    onBreadcrumbClick,
    onDocumentationClick,
}: ConceptHeaderProps) {
    const breadcrumbs = buildBreadcrumb(item, tree);

    // Derive badges
    const badges: Array<{ label: string; color: string }> = [];
    badges.push({ label: snakeToPascal(item.type), color: 'bg-slate-100 text-slate-600' });

    const hasTimeSeries = Object.values(item.fields || {}).some((f: any) => f.type === 'time_serie');
    if (hasTimeSeries) {
        badges.push({ label: 'Time Series', color: 'bg-blue-50 text-blue-600' });
    }

    const hasComputeError = Object.values(item.fields || {}).some((f: any) => f.compute_error);
    if (hasComputeError) {
        badges.push({ label: 'Compute Error', color: 'bg-red-50 text-red-600' });
    }

    return (
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4">
            {/* Breadcrumb */}
            {breadcrumbs.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-slate-400 mb-1 flex-wrap">
                    {breadcrumbs.map((crumb, i) => (
                        <React.Fragment key={crumb.id}>
                            {i > 0 && <span>/</span>}
                            <button
                                onClick={() => onBreadcrumbClick(crumb.id)}
                                className="hover:text-primary hover:underline transition-colors"
                            >
                                {crumb.name}
                            </button>
                        </React.Fragment>
                    ))}
                    <span>/</span>
                </div>
            )}

            {/* Title */}
            <h2 className="text-xl font-bold text-slate-900">{item.name}</h2>

            {/* Badges + Actions */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
                {badges.map((b) => (
                    <span
                        key={b.label}
                        className={cn('text-xs font-medium px-2 py-0.5 rounded', b.color)}
                    >
                        {b.label}
                    </span>
                ))}

                {/* Spacer */}
                <div className="flex-1" />

                {/* Documentation button */}
                {item.documentation && (
                    <button
                        onClick={onDocumentationClick}
                        className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary transition-colors"
                    >
                        <span className="material-symbols-outlined text-[16px]">description</span>
                        Documentation
                    </button>
                )}
            </div>
        </div>
    );
}
